import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Box,
    Grid,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useMutation } from '@tanstack/react-query';
import MessageCommentList from './MessageCommentList';
import { AppContext } from '../../../sporttia/AppContext';
import { SttLabelDate } from '../../../sporttia/labels/SttLabelDate';
import useMessagesService from '../../../services/MessagesService';

type Receptor = {
    user?: {
        name: string;
    };
    group?: {
        name: string;
    };
    sc?: {
        name: string;
    };
};

type Message = {
    id: number;
    display?: string;
    sender?: {
        fullName: string;
    };
    img?: {
        id: number;
    };
    created: string;
    text: string;
    numUnread: number;
    comments?: {
        count: number;
    };
    receptors?: {
        rows: Receptor[];
    };
};

type Props = {
    message: Message;
    fold: string;
    onUpdate: () => void;
};

/**
 * Representation of a message that will later be rendered in a list.
 * @param message Message object.
 * @param fold Fold type (INBOX|...).
 * @param onUpdate Launched when a message is updated.
 * @returns {React.ReactElement}
 */
export default function MessageItem({
    message,
    fold,
    onUpdate,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const messagesService = useMessagesService();
    const [showCommentsMessage, setShowCommentsMessage] =
        useState<Message | null>();
    const updateMessageReadMutation = useMutation({
        mutationFn: (params: {
            id: number;
            params?: Record<string, unknown>;
        }) =>
            messagesService.updateMessagesRead(
                params?.id,
                params?.params as any,
            ),
        onSuccess: onUpdate,
    });

    const isDeletedUserName = (name: string | undefined | null): boolean =>
        !name || name.startsWith('[[');

    const getDisplayName = (name: string | undefined | null): string =>
        isDeletedUserName(name) ? t('DeletedUser') : name!;

    const buildMsgTitle = (msg: Message): string => {
        if (fold === 'INBOX') {
            const name = msg?.sender?.fullName || msg?.display;
            return getDisplayName(name);
        }
        return msg?.receptors
            ? msg?.receptors?.rows
                  .map((receptor) => {
                      const name =
                          receptor?.user?.name ||
                          receptor?.group?.name ||
                          receptor?.sc?.name;
                      return getDisplayName(name);
                  })
                  .filter((value, index, self) => self.indexOf(value) === index)
                  .join(', ')
            : t('None');
    };

    return (
        <>
            <ListItem alignItems="flex-start">
                <ListItemAvatar>
                    <Avatar
                        alt={getDisplayName(
                            message?.sender?.fullName || message?.display,
                        )}
                        src={cxt?.getResource(
                            message?.img?.id
                                ? `/images/${message?.img?.id}.jpg`
                                : `/static/images/avatar/1.jpg`,
                        )}
                    />
                </ListItemAvatar>

                <ListItemText
                    primary={buildMsgTitle(message)}
                    secondary={
                        <Typography variant="body2" color="textPrimary">
                            <SttLabelDate
                                date={message?.created}
                                timezoneName={cxt?.sc?.timezone?.name}
                                locale={cxt?.lang?.key}
                            />
                        </Typography>
                    }
                />
            </ListItem>

            <Box ml={9}>
                <Box
                    my={2}
                    pl={2}
                    style={{
                        borderLeft: 2,
                        borderLeftStyle: 'solid',
                        borderColor: message?.numUnread > 0 ? 'red' : '#fafafa',
                    }}
                >
                    <Typography variant="body1">{message?.text}</Typography>
                </Box>

                <Box mb={2}>
                    <Grid container spacing={3}>
                        <Grid size={{ md: 2 }}>
                            {message?.comments?.count &&
                                message?.comments?.count > 0 && (
                                    <Button
                                        size="small"
                                        startIcon={<ChatBubbleOutlineIcon />}
                                        onClick={() =>
                                            setShowCommentsMessage(
                                                showCommentsMessage
                                                    ? null
                                                    : message,
                                            )
                                        }
                                    >
                                        {t('comments', {
                                            count: message?.comments?.count,
                                        })}
                                    </Button>
                                )}

                            {fold === 'INBOX' &&
                                message?.comments?.count === 0 && (
                                    <Button
                                        size="small"
                                        startIcon={<ReplyIcon />}
                                        onClick={() =>
                                            setShowCommentsMessage(message)
                                        }
                                    >
                                        {t('Reply')}
                                    </Button>
                                )}
                        </Grid>

                        <Grid size={{ md: 3 }}>
                            {message?.numUnread > 0 && (
                                <Button
                                    size="small"
                                    startIcon={<DoneAllIcon />}
                                    onClick={() =>
                                        updateMessageReadMutation.mutate({
                                            id: message?.id,
                                        } as any)
                                    }
                                >
                                    {t('MarkAsRead')}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {showCommentsMessage?.id === message?.id && (
                    <MessageCommentList message={message} />
                )}
            </Box>
        </>
    );
}
